import { Block, BlockContent, DEFAULT_CONFIG } from '../types';

export class BlockService {
  // Générer un ID unique
  static generateId(): string {
    return `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Créer un nouveau bloc
  static createBlock(
    parentId?: string,
    level: number = 0,
    content: Partial<BlockContent> = {},
    blockType?: 'titre' | 'prestation'
  ): Block {
    return {
      id: this.generateId(),
      parentId,
      order: 0,
      level,
      blockType,
      isCollapsed: false,
      children: [],
      content: {
        titre: content.titre || DEFAULT_CONFIG.defaultSectionTitle,
        ...content
      }
    };
  }

  // Créer une prestation
  static createPrestation(parentId?: string, level: number = 0): Block {
    return this.createBlock(parentId, level, {
      ...DEFAULT_CONFIG.defaultPrestationContent
    }, 'prestation');
  }

  // Créer une section (titre/lot)
  static createSection(parentId?: string, level: number = 0): Block {
    return this.createBlock(parentId, level, {
      titre: DEFAULT_CONFIG.defaultSectionTitle,
      prixTotal: '0'
    }, 'titre');
  }

  // Mettre à jour un bloc
  static updateBlock(block: Block, updates: Partial<Block>): Block {
    return { ...block, ...updates };
  }

  // Supprimer un bloc et ses enfants
  static deleteBlock(blocks: Record<string, Block>, blockId: string): Record<string, Block> {
    const block = blocks[blockId];
    if (!block) return blocks;

    const newBlocks = { ...blocks };
    
    // Supprimer le bloc
    delete newBlocks[blockId];

    // Supprimer tous les enfants récursivement
    const deleteChildren = (children: string[]) => {
      children.forEach(childId => {
        if (newBlocks[childId]) {
          deleteChildren(newBlocks[childId].children);
          delete newBlocks[childId];
        }
      });
    };

    deleteChildren(block.children);
    return newBlocks;
  }

  // Déplacer un bloc
  static moveBlock(
    blocks: Record<string, Block>,
    blockId: string,
    newParentId?: string,
    newOrder?: number
  ): Record<string, Block> {
    const block = blocks[blockId];
    if (!block) return blocks;

    const newBlocks = { ...blocks };

    // Retirer de l'ancien parent
    if (block.parentId) {
      const oldParent = newBlocks[block.parentId];
      if (oldParent) {
        newBlocks[block.parentId] = {
          ...oldParent,
          children: oldParent.children.filter(id => id !== blockId)
        };
      }
    }

    // Ajouter au nouveau parent
    if (newParentId) {
      const newParent = newBlocks[newParentId];
      if (newParent) {
        newBlocks[newParentId] = {
          ...newParent,
          children: [...newParent.children, blockId]
        };
      }
    }

    // Mettre à jour le bloc
    newBlocks[blockId] = {
      ...block,
      parentId: newParentId,
      level: newParentId ? (newBlocks[newParentId]?.level || 0) + 1 : 0,
      order: newOrder || 0
    };

    return newBlocks;
  }

  // Obtenir les enfants d'un bloc
  static getChildren(blocks: Record<string, Block>, parentId: string): Block[] {
    const parent = blocks[parentId];
    if (!parent) return [];
    
    return parent.children
      .map(childId => blocks[childId])
      .filter(Boolean)
      .sort((a, b) => a.order - b.order);
  }

  // Obtenir le parent d'un bloc
  static getParent(blocks: Record<string, Block>, blockId: string): Block | null {
    const block = blocks[blockId];
    if (!block || !block.parentId) return null;
    
    return blocks[block.parentId] || null;
  }

  // Synchroniser les niveaux de tous les blocs
  static syncLevels(blocks: Record<string, Block>, rootBlocks: string[]): Record<string, Block> {
    const updatedBlocks = { ...blocks };
    
    const updateLevelsRecursively = (blockIds: string[], currentLevel: number = 0) => {
      const sortedBlocks = blockIds
        .map(blockId => updatedBlocks[blockId])
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);
      
      sortedBlocks.forEach(block => {
        // Mettre à jour le niveau du bloc
        if (block.level !== currentLevel) {
          updatedBlocks[block.id] = { ...block, level: currentLevel };
        }
        
        // Mettre à jour les enfants récursivement
        if (block.children.length > 0) {
          updateLevelsRecursively(block.children, currentLevel + 1);
        }
      });
    };
    
    updateLevelsRecursively(rootBlocks, 0);
    return updatedBlocks;
  }

  // Valider et corriger les relations parent-enfant
  static validateAndFixRelations(blocks: Record<string, Block>, rootBlocks: string[]): Record<string, Block> {
    const updatedBlocks = { ...blocks };
    
    // Pour chaque bloc, vérifier que ses enfants ont le bon parentId
    Object.values(updatedBlocks).forEach(block => {
      block.children.forEach(childId => {
        const child = updatedBlocks[childId];
        if (child && child.parentId !== block.id) {
          updatedBlocks[childId] = { ...child, parentId: block.id };
        }
      });
    });
    
    return updatedBlocks;
  }

  // Obtenir tous les blocs dans l'ordre d'affichage
  static getBlocksInOrder(blocks: Record<string, Block>, rootBlocks: string[]): Block[] {
    const result: Block[] = [];
    const processedBlocks = new Set<string>(); // Protection contre les boucles infinies
    
    const addBlocksRecursively = (blockIds: string[], currentLevel: number = 0) => {
      const sortedBlocks = blockIds
        .map(blockId => blocks[blockId])
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);
      
      sortedBlocks.forEach(block => {
        // Éviter les boucles infinies
        if (processedBlocks.has(block.id)) {
          console.warn(`Bloc ${block.id} déjà traité - référence circulaire détectée`);
          return;
        }
        
        processedBlocks.add(block.id);
        
        // Mettre à jour le niveau du bloc s'il ne correspond pas
        const updatedBlock = { ...block, level: currentLevel };
        result.push(updatedBlock);
        
        // Si c'est une section non fermée, ajouter ses enfants
        if (block.children.length > 0 && !block.isCollapsed) {
          addBlocksRecursively(block.children, currentLevel + 1);
        }
      });
    };
    
    addBlocksRecursively(rootBlocks, 0);
    return result;
  }
}
